import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/common/EmptyState";
import { FlashcardReview } from "@/components/learning/FlashcardReview";
import { flashcardsApi } from "@/lib/apiClient";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

export default function Review() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [cards, setCards] = useState(null);
    const [loading, setLoading] = useState(true);
    const [done, setDone] = useState(false);
    const [stats, setStats] = useState({ reviewed: 0, again: 0 });

    useEffect(() => {
        (async () => {
            try { setCards(await flashcardsApi.due()); }
            catch (e) { toast.error(t("review.loadFailed")); }
            finally { setLoading(false); }
        })();
    }, [t]);

    const onRate = async (card, rating) => {
        await flashcardsApi.review(card.id, rating);
        setStats((s) => ({ reviewed: s.reviewed + 1, again: s.again + (rating === "again" ? 1 : 0) }));
    };

    if (loading) return <div className="flex min-h-screen items-center justify-center bg-surface-0"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" /></div>;

    return (
        <div className="min-h-screen bg-surface-0">
            <header className="sticky top-0 z-20 border-b border-border bg-surface-0/85 backdrop-blur-md">
                <div className="mx-auto flex max-w-xl items-center gap-3 px-4 py-3">
                    <Button data-testid="review-back-button" variant="ghost" size="icon" className="text-t2" onClick={() => navigate("/learn")}><ArrowLeft className="h-5 w-5" /></Button>
                    <h1 className="font-sans-display text-base font-semibold text-t1">{t("review.title")}</h1>
                </div>
            </header>

            <div className="mx-auto max-w-xl px-4 py-8">
                {(!cards || cards.length === 0) ? (
                    <EmptyState
                        testid="review-empty"
                        icon={CheckCircle2}
                        title={t("review.allCaughtTitle")}
                        body={t("review.allCaughtBody")}
                        cta={t("review.backToLearning")}
                        ctaIcon={Sparkles}
                        onCta={() => navigate("/learn")}
                    />
                ) : done ? (
                    <div className="flex flex-col items-center justify-center rounded-3xl border border-border bg-surface-1 px-6 py-16 text-center" data-testid="review-complete">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"><CheckCircle2 className="h-9 w-9" /></div>
                        <h3 className="mt-4 font-sans-display text-xl font-semibold text-t1">{t("review.completeTitle")}</h3>
                        <p className="mt-1.5 text-sm text-t3">
                            {stats.again > 0
                                ? t("review.completeBodyNeed", { n: stats.reviewed, again: stats.again })
                                : t("review.completeBodyGreat", { n: stats.reviewed })}
                        </p>
                        <Button className="mt-5" onClick={() => navigate("/learn")}>{t("review.backToLearning")}</Button>
                    </div>
                ) : (
                    <FlashcardReview cards={cards} onRate={onRate} onDone={() => setDone(true)} />
                )}
            </div>
        </div>
    );
}
